import { Component, OnDestroy } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { TranslateService } from '@ngx-translate/core';

/**
 * Action buttons for the CIL Configuration master tables (Type / Tool /
 * Frequency).
 *
 * This is a copy of BtnCellRendererQualityParameter with translated tooltips
 * added. It is deliberately a separate component: the original is shared by
 * fifteen screens across the app and must not change.
 *
 * The host grids dispatch clicks with
 *   params.event.target.textContent === 'edit' | 'delete'
 * so the <mat-icon> content and the element structure are reproduced exactly.
 * Only the tooltip bindings are new -- pTooltip is an attribute directive and
 * contributes no text, so textContent is unaffected.
 *
 * Tooltip labels come from cellRendererParams:
 *   page, editKey / editFallback, deleteKey / deleteFallback, getLangObj
 *
 * `page` selects the i18n section, so the same renderer serves the master
 * grids on CIL Configuration and Defect Configuration.
 */
@Component({
  // tslint:disable-next-line:component-selector
  selector: 'btn-cell-rendererCilMasterTable',
  template: `
    <button
      mat-mini-fab
      color="primary"
      tooltipPosition="top"
      [pTooltip]="editTooltip"
      (click)="onClick($event)"
    >
      <mat-icon>edit</mat-icon>
    </button>
    <button
      mat-mini-fab
      color="warn"
      tooltipPosition="top"
      [pTooltip]="deleteTooltip"
      (click)="onClick($event)"
      [disabled]="paramAccess"
    >
      <mat-icon>delete</mat-icon>
    </button>
  `,
})
// tslint:disable-next-line:component-class-suffix
export class BtnCellRendererCilMasterTable
  implements ICellRendererAngularComp, OnDestroy {

  /** i18n section for the host page, supplied via cellRendererParams.page */
  private page = 'CILConfiguration';

  private params: any;
  public paramAccess: any;
  public editTooltip = '';
  public deleteTooltip = '';

  constructor(public translate: TranslateService) {}

  refresh(params: any): boolean {
    // Returning false tells ag-Grid to destroy and rebuild this cell, which
    // re-runs agInit and re-resolves the tooltips. It must not throw: the page
    // calls refreshCells({ force: true }) when the language changes.
    return false;
  }

  agInit(params: any): void {
    this.params = params;
    if (params && params.page) {
      this.page = params.page;
    }
    this.paramAccess = params && params.data ? params.data.isDisable : false;
    this.editTooltip = this.t(params.editKey, params.editFallback);
    this.deleteTooltip = this.t(params.deleteKey, params.deleteFallback);
  }

  /** langObj (via cellRendererParams.getLangObj) -> "<PAGE>.<key>" -> flat key -> English. */
  private t(key: string, fallback: string): string {
    if (!key) {
      return fallback || '';
    }
    const langObj =
      this.params && typeof this.params.getLangObj === 'function'
        ? this.params.getLangObj()
        : null;
    if (langObj && langObj[key]) {
      return langObj[key];
    }
    for (const candidate of [`${this.page}.${key}`, key]) {
      const value = this.translate.instant(candidate);
      if (typeof value === 'string' && value !== candidate) {
        return value;
      }
      if (value && typeof value === 'object' && typeof value.title === 'string') {
        return value.title;
      }
    }
    return fallback || '';
  }

  onClick($event: any): any {
    if (this.params.onClick instanceof Function) {
      // put anything into params u want pass into parents component
      const params = {
        event: $event,
        rowData: this.params.node.data,
        // ...something
      };
      this.params.onClick(params);
    }
  }

  ngOnDestroy(): any {
    // no need to remove the button click handler
    // https://stackoverflow.com/questions/49083993/does-angular-automatically-remove-template-event-listeners
  }
}
